import useOneTimeEmail from "../../state/hooks/one-time-emails/useOneTimeEmail";
import { Formik } from "formik";
import Button from "../../commons/Button/Button";
import { notification } from "antd";

type InitialValuesType = {
  timeToReceive: string;
  newsletter: "yes" | "no" | "both";
  subscribeToLearningEmails: "yes" | "no" | "both";
  sendTo: "basic" | "premium" | "all";
  emailType: "marketing" | "feature" | "legal";
  olderThanDays: number;
  earlierThanDays: number;
  premiumTobeCancelled: boolean;
  premiumNotTobeCancelled: boolean;
  spreadTime: number;
};
const initialValue: InitialValuesType = {
  timeToReceive: "",
  newsletter: "yes",
  subscribeToLearningEmails: "both",
  sendTo: "basic",
  emailType: "marketing",
  olderThanDays: 0,
  earlierThanDays: 0,
  premiumTobeCancelled: false,
  premiumNotTobeCancelled: false,
  spreadTime: 0,
};
const OneTimeEmail = () => {
  const { mutate: mutateAsync } = useOneTimeEmail({
    onSuccess: () => {
      notification.success({
        message: "Success",
        description: "Email sent successfully",
      });
    },
    onError: () => {
      notification.error({
        message: "Error",
        description: "Error sending email",
      });
    },
  });

  return (
    <div className="flex justify-center items-center h-screen">
      <Formik
        initialValues={initialValue}
        validateOnChange={true}
        validate={(values) => {
          const errors: Record<string, string> = {};
          if (!values.timeToReceive) {
            errors.timeToReceive = "Time to receive is required";
          } else {
            const timeToReceiveRegex = /^(0?[1-9]|1[0-2]):[0-5]?[0-9](AM|PM)$/;
            if (!timeToReceiveRegex.test(values.timeToReceive)) {
              errors.timeToReceive = "Invalid time format. Use HH:MMAM/PM format";
            }
          }
          return errors;
        }}
        onSubmit={(values) => {
          const timeToReceiveRegex = /^(0?[1-9]|1[0-2]):[0-5]?[0-9](AM|PM)$/;
          if (
            values.timeToReceive &&
            timeToReceiveRegex.test(values.timeToReceive)
          ) {
            const lastSubmittedEmails = localStorage.getItem(
              "lastSubmittedEmails"
            );
            const now = new Date().getTime();

            if (
              !lastSubmittedEmails ||
              now - parseInt(lastSubmittedEmails) > 2 * 60 * 1000
            ) {
              localStorage.setItem("lastSubmittedEmails", now.toString());
              mutateAsync({
                timeToReceive: values.timeToReceive as `${string}${"AM" | "PM"}`,
                olderThanDays: +values.olderThanDays,
                earlierThanDays: +values.earlierThanDays,
                subscribeToLearningEmails:
                  values.subscribeToLearningEmails === "both"
                    ? undefined
                    : values.subscribeToLearningEmails === "yes",
                newsletter:
                  values.newsletter === "both"
                    ? undefined
                    : values.newsletter === "yes",
                premium: values.sendTo === "basic" ? false : undefined,
                premiumTobeCancelled: values.sendTo === "all" ? undefined : (values.premiumTobeCancelled || undefined),
                premiumNotTobeCancelled: values.sendTo === "all" ? undefined : (values.premiumNotTobeCancelled || undefined),
                emailType: values.emailType,
                spreadTime: values.spreadTime,
              });
            } else {
              alert("Please wait for 2 minutes before submitting again.");
            }
          }
        }}
      >
        {({
          values,
          setFieldValue,
          handleSubmit,
          handleBlur,
          setFieldTouched,
          errors,
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="bg-white p-8 rounded shadow-md w-96"
            >
              <div className="mb-4">
                <label className="block">Time to receive</label>
                <input
                  type="text"
                  name="timeToReceive"
                  placeholder="Enter time to receive (HH:MMAM/PM)"
                  value={values.timeToReceive}
                  onChange={(e) => {
                    setFieldTouched("timeToReceive", true);
                    setFieldValue("timeToReceive", e.target.value);
                  }}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                <div className="text-gray-500 text-xs mt-1">
                  Format: 10:30AM or 2:45PM
                </div>
                {errors.timeToReceive && (
                  <div className="text-error text-sm mt-1">
                    {errors.timeToReceive}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block">Older Than (days)</label>
                <input
                  type="number"
                  name="olderThanDays"
                  placeholder="Enter older than (days)"
                  min={0}
                  value={values.olderThanDays}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = parseInt(value, 10);
                    setFieldValue("olderThanDays", numValue >= 0 ? numValue : 0);
                  }}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {errors.olderThanDays && (
                  <div className="text-error text-sm mt-1">
                    {errors.olderThanDays}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block">Earlier Than (days)</label>
                <input
                  type="number"
                  name="earlierThanDays"
                  placeholder="Enter earlier than (days)"
                  min={0}
                  value={values.earlierThanDays}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = parseInt(value, 10);
                    setFieldValue("earlierThanDays", numValue >= 0 ? numValue : 0);
                  }}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block">Spread Time</label>
                <input
                  type="number"
                  name="spreadTime"
                  placeholder="Enter spread time"
                  min={0}
                  value={values.spreadTime}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (value >= 1 || e.target.value === "") {
                      setFieldValue("spreadTime", value >= 1 ? value : 1);
                    }
                  }}
                  onBlur={handleBlur}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block">Email Type</label>
                <select
                  value={values.emailType}
                  onChange={(e) => {
                    setFieldTouched("emailType", true);
                    setFieldValue("emailType", e.target.value);
                  }}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="marketing">marketing</option>
                  <option value="feature">feature</option>
                  <option value="legal">legal</option>
                </select>
              </div>
              <div>
                <label>learning - (subscribeToLearningEmails)</label>
                <div className="mb-4 flex-row flex justify-around ">
                  <label className={"flex items-center"}>
                    <input
                      type="checkbox"
                      name="subscribeToLearningEmails"
                      checked={values.subscribeToLearningEmails === "yes"}
                      onChange={(e) => {
                        setFieldValue("subscribeToLearningEmails", "yes");
                      }}
                      className="mr-2"
                    />
                    YES
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="subscribeToLearningEmails"
                      checked={values.subscribeToLearningEmails === "no"}
                      onChange={(e) => {
                        setFieldValue("subscribeToLearningEmails", "no");
                      }}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="subscribeToLearningEmails"
                      checked={values.subscribeToLearningEmails === "both"}
                      onChange={(e) => {
                        setFieldValue("subscribeToLearningEmails", "both");
                      }}
                      className="mr-2"
                    />
                    both
                  </label>
                </div>
              </div>
              <div>
                <label>marketing - (newsletter)</label>
                <div className="mb-4 flex-row flex justify-around ">
                  <label className={"flex items-center"}>
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={values.newsletter === "yes"}
                      onChange={(e) => {
                        setFieldValue("newsletter", "yes");
                      }}
                      className="mr-2"
                    />
                    YES
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={values.newsletter === "no"}
                      onChange={(e) => {
                        setFieldValue("newsletter", "no");
                      }}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={values.newsletter === "both"}
                      onChange={(e) => {
                        setFieldValue("newsletter", "both");
                      }}
                      className="mr-2"
                    />
                    both
                  </label>
                </div>
              </div>
              <div>
                <label>Premium To Be Cancelled</label>
                <div className="mb-4 flex-row flex justify-around ">
                  <label className={"flex items-center"}>
                    <input
                      type="checkbox"
                      name="premiumTobeCancelled"
                      checked={values.sendTo === "all" ? false : values.premiumTobeCancelled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue("premiumTobeCancelled", true);
                          setFieldValue("premiumNotTobeCancelled", false);
                        } else {
                          setFieldValue("premiumTobeCancelled", false);
                        }
                      }}
                      disabled={values.sendTo === "all"}
                      className="mr-2"
                    />
                    Premium To Be Cancelled
                  </label>
                </div>
              </div>
              <div>
                <label>Premium Not To Be Cancelled</label>
                <div className="mb-4 flex-row flex justify-around ">
                  <label className={"flex items-center"}>
                    <input
                      type="checkbox"
                      name="premiumNotTobeCancelled"
                      checked={values.sendTo === "all" ? false : values.premiumNotTobeCancelled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue("premiumNotTobeCancelled", true);
                          setFieldValue("premiumTobeCancelled", false);
                        } else {
                          setFieldValue("premiumNotTobeCancelled", false);
                        }
                      }}
                      disabled={values.sendTo === "all"}
                      className="mr-2"
                    />
                    Premium Not To Be Cancelled
                  </label>
                </div>
              </div>
              <div>
                <label>Send To</label>
                <div className="mb-4 flex-row flex justify-around ">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="sendTo"
                      checked={values.sendTo === "basic"}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue("sendTo", "basic");
                        } else {
                          setFieldValue("sendTo", null);
                        }
                      }}
                      className="mr-2"
                    />
                    basic
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="sendTo"
                      checked={values.sendTo === "all"}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue("sendTo", "all");
                        } else {
                          setFieldValue("sendTo", null);
                        }
                      }}
                      className="mr-2"
                    />
                    all
                  </label>
                </div>
              </div>

              <Button buttonType="submit" type="main">
                Submit
              </Button>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default OneTimeEmail;
